import React, { FC, useRef, createContext, useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useInView } from 'react-intersection-observer';
import qs from 'qs';

// types
import {
	ISearchOrganisationByAdminResponse,
	ISearchOrganizationByAdminRequest,
	ORGANISATION_ACTIVE_STATUS,
	SortDirection,
	IFullTextSearchOrganisationByAdmin,
	SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH,
	FullTextSearchOrganisationByAdmin,
	SearchOrganisationByAdminResponse,
	ISearchOrganizationByAdminSort,
	SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT,
	SearchOrganizationByAdminSort,
	SearchOrganizationByAdminRequest,
} from '@joc/api-gateway';

// hooks
import { useOrganizations } from 'hooks/organizations';

// core
import { formatDate, generateLocation, setFullName, setSearchOrSortQueryString } from 'core/functions';
import { resolveNextSort } from 'core/functions/sort';
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES, formatOrganisationGeneralType } from 'core/constants';

// components
import TableHead from 'shared/components/Table/TableHead';
import TableRow from 'shared/components/Table/TableRow';
import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import TableBody from 'shared/components/Table/TableBody';
import TableMain from 'shared/components/Table/TableMain';
import TableMenu from 'components/Organization/TableMenu';
import Loader from 'shared/components/Loader';
import ResponseFailure from 'shared/components/ResponseFailure';
import OrgActionCell from 'components/Organization/Volunteers/Table/OrgActionCell';
import ContactCell from 'shared/components/Table/CellRenderers/ContactCell';
import CellDefault from 'shared/components/Table/CellRenderers/CellDefault';
import OpportunityNameCell from 'shared/components/Table/CellRenderers/OpportunityNameCell';
import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import OrgContactCell from 'shared/components/Table/CellRenderers/OrgContactCell';
import TableHeadCellSortable from '../TableHead/TableHeadCellSortable';
import Actions from './Actions';

// styles
import styles from './AdminOrgTable.module.scss';
import { useToggle } from 'react-use';

export const OrganizationsTableContext = createContext({} as ISearchOrganisationByAdminResponse);

const parseQueryString = (search: string) => {
	const searchParams: ISearchOrganizationByAdminRequest = qs.parse(search, {
		ignoreQueryPrefix: true,
	});
	return searchParams;
};

const OrganizationsTable: FC = () => {
	const history = useHistory();
	const location = useLocation();
	const printRef = useRef<HTMLTableElement>(null);

	const [totalFetched, setTotalFetched] = useState(0);

	const [search, setSearch] = useState<FullTextSearchOrganisationByAdmin>();
	const [sort, setSort] = useState<SearchOrganizationByAdminSort>();

	const searchBody = useMemo(
		() =>
			SearchOrganizationByAdminRequest.fromJS({
				sort: sort?.sortBy ? sort : undefined,
				fullTextSearch: !!search?.value ? search : undefined,
			}),
		[search, sort]
	);

	const [isAllTime, toggleIsAllTime] = useToggle(true);

	const sortByOpportunitiesCount = useMemo(() => {
		return isAllTime
			? SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalOpportunities
			: SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.YearOpportunities;
	}, [isAllTime]);

	const sortByOpportunitiesHours = useMemo(() => {
		return isAllTime
			? SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalOpportunitiesHours
			: SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.YearOpportunitiesHours;
	}, [isAllTime]);

	const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useOrganizations(
		search,
		sort
	);

	const { ref, inView } = useInView({
		threshold: 0,
		delay: 200,
	});

	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage().finally();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	useEffect(() => {
		setTotalFetched(
			([] as Array<SearchOrganisationByAdminResponse>).concat(...(data?.pages?.map(page => page.records) ?? []))
				.length
		);
	}, [data]);

	useEffect(() => {
		const d = parseQueryString(location.search);

		setSearch(d.fullTextSearch);
		setSort(SearchOrganizationByAdminSort.fromJS(d.sort || {}));
	}, [location.search]);

	const buttonFailureClickHandler = async () => {
		history.push({ search: '' });
	};

	const appendQueryString = useCallback(
		(
			newQueryParams: IFullTextSearchOrganisationByAdmin | ISearchOrganizationByAdminSort,
			searchBy?: string
		): void => {
			const searchQuery = setSearchOrSortQueryString(location.search, newQueryParams, searchBy);
			history.push({ search: searchQuery });
		},
		[history, location.search]
	);

	const searchChangeHandler = (value: string): void => {
		const values = Object.values(SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH);
		const fields = values.filter(v => v !== SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SEARCH.Id);
		const fullTextSearchParams: IFullTextSearchOrganisationByAdmin = { value, fields };
		if (fullTextSearchParams.value.length) {
			appendQueryString(fullTextSearchParams, 'fullTextSearch');
		} else {
			const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
			delete searchParams.fullTextSearch;
			history.push({ search: qs.stringify(searchParams) });
		}
	};

	const descFirstSortFields = useMemo(
		() =>
			new Set([
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.CreateDate,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalVolunteers,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalUsers,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalOpportunities,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.YearOpportunities,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalOpportunitiesHours,
				SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.YearOpportunitiesHours,
			]),
		[]
	);

	const handleSortChange = useCallback(
		(sortBy: SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT, clickedDirection?: SortDirection) => {
			setSort(prevSort => {
				const nextSort = resolveNextSort({
					currentSort: prevSort,
					nextSortBy: sortBy,
					clickedDirection,
					descFirstSortFields,
					allowClear: true,
				});

				if (!nextSort) {
					const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
					delete searchParams.sort;
					history.replace({ search: qs.stringify(searchParams) });
					return undefined;
				}

				appendQueryString(nextSort, 'sort');
				return SearchOrganizationByAdminSort.fromJS(nextSort);
			});
		},
		[appendQueryString, descFirstSortFields, history, location.search, setSort]
	);

	const onToggleIsAllTime = useCallback(() => {
		if (sort?.sortBy === sortByOpportunitiesCount) handleSortChange(sortByOpportunitiesCount, sort?.sortDir);

		if (sort?.sortBy === sortByOpportunitiesHours) handleSortChange(sortByOpportunitiesHours, sort?.sortDir);

		toggleIsAllTime();
	}, [toggleIsAllTime, handleSortChange, sort, sortByOpportunitiesCount, sortByOpportunitiesHours]);

	return (
		<div className={styles.container}>
			<div className={styles.table}>
				<TableMenu searchChangeHandler={searchChangeHandler}>
					<Actions
						isAllTime={isAllTime}
						onToggleIsAllTime={onToggleIsAllTime}
						isAllowExport={!!data?.pages?.[0]?.total}
						searchBody={searchBody}
					/>
				</TableMenu>
				{!isFetching && !totalFetched ? (
					<ResponseFailure
						styleTable={true}
						message={'We couldn`t find the volunteers you were looking for this time.'}
						buttonClickHandler={buttonFailureClickHandler}
						buttonTitle="Get all"
					/>
				) : (
					<TableMain ref={printRef}>
						<TableHead callChild="organizations">
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.logo}
								text={TABLE_HEADERS.logo}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.orgName}
								sortBy={SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.OrganizationName}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.date}
								sortBy={SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.CreateDate}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.location}
								text={TABLE_HEADERS.location}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.typeOrg}
								sortBy={SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.OrganisationGeneralType}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.contacts}
								text={TABLE_HEADERS.contacts}
								isSortDisabled={true}
							/>
							<TableHeadCell
								itemClassName={TABLE_ITEMS_CLASSNAMES.owner}
								text={TABLE_HEADERS.owner}
								isSortDisabled={true}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.totalVolunteers}
								sortBy={SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalVolunteers}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.totalUsers}
								sortBy={SEARCH_ORGANIZATION_BY_ADMIN_POSSIBLE_SORT.TotalUsers}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.totalOppo}
								sortBy={sortByOpportunitiesCount}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCellSortable
								title={TABLE_HEADERS.totalOppoHours}
								sortBy={sortByOpportunitiesHours}
								handleSortChange={handleSortChange}
								sort={sort}
							/>
							<TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />
							<TableHeadCell itemClassName={TABLE_ITEMS_CLASSNAMES.options} />
						</TableHead>
						<TableBody>
							{data?.pages.flatMap(page =>
								page?.records.map(organization => (
									<TableRow
										key={organization.id}
										callChild="organizations"
										suspend={
											organization.organizationActiveStatus.status ===
											ORGANISATION_ACTIVE_STATUS.SUSPENDED
										}
									>
										<AvatarCell imagePath={organization.organizationLogoPath} />
										<OpportunityNameCell
											opportunityName={organization.organizationName}
											opportunityType={+organization.id}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.date}
											text={formatDate(organization?.createDate)}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.location}
											text={generateLocation(organization.address)}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.type}
											text={formatOrganisationGeneralType(organization.organisationGeneralType)}
										/>
										<OrgContactCell
											phone={organization.organizationPhoneNumber}
											email={organization.organizationEmail}
											site={organization.organizationWebSite}
										/>
										<OrgContactCell
											contactFullName={setFullName(
												organization.owner.firstName,
												organization.owner.lastName
											)}
											phone={organization.owner?.phoneNumber}
											email={organization.owner?.email}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.totalVolunteers}
											text={organization.totalVolunteers}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.totalUsers}
											text={organization.totalUsers}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.totalOppos}
											text={
												isAllTime
													? organization.totalOpportunities
													: organization.yearOpportunities
											}
										/>
										<CellDefault
											parentClassName={TABLE_ITEMS_CLASSNAMES.totalOppoHours}
											text={
												isAllTime
													? Math.floor(organization.totalOpportunitiesHours)
													: Math.floor(organization.yearOpportunitiesHours)
											}
										/>
										<div className={styles.actions__content}>
											<OrganizationsTableContext.Provider value={organization}>
												<OrgActionCell organizationId={organization.id} />
											</OrganizationsTableContext.Provider>
										</div>
										<ContactCell
											chatId={organization.chatId || ''}
											orgId={organization.id}
											users={true}
											organizations={true}
										/>
									</TableRow>
								))
							)}
						</TableBody>
					</TableMain>
				)}

				{isFetchingNextPage || status === 'pending' ? (
					<div style={{ margin: '32px 0' }}>
						<Loader />
					</div>
				) : null}

				{!isFetching && !isLoading && !isFetchingNextPage && (
					<tr style={{ height: 1 }} ref={ref}>
						<td style={{ padding: 0 }}>
							<div style={{ height: '1px', visibility: 'hidden' }} />
						</td>
					</tr>
				)}
			</div>
		</div>
	);
};

export default OrganizationsTable;
